<i18n>
{
  "fr": {
    "configuratorLayerEditorModalImageImport": {
      "instructions": {
        "formats": "Taille optimale de fichier recommandée\u00a0: {width}px\u00a0⨉\u00a0{height}px.",
        "sizes": "Formats autorisés\u00a0: {formatList}.",
        "glue": " et "
      },
      "screenPrinting": {
        "warning": "Attention\u00a0: votre visuel sera converti en une seule couleur",
        "details": "Vous avez sélectionné\u00a0: visuel en une seule couleur d'impression"
      },
      "fromFile": {
        "label": "Sélectionner un fichier"
      },
      "aiGenImage": {
        "label": "Générer une image avec IA"
      },
      "action": {
        "moreInfo": "Plus d'informations"
      },
      "file": {
        "error": {
          "invalid": "Le fichier sélectionné est invalide.",
          "invalidSize": "@:configuratorLayerEditorModalImageImport.file.error.invalid La taille maximale autorisée est {size}.",
          "invalidType": {
            "message": "@:configuratorLayerEditorModalImageImport.file.error.invalid (formats autorisés\u00A0: {formats}).",
            "glue": " et "
          }
        }
      }
    }
  }
}
</i18n>

<template>
  <div class="configurator-layer-editor-modal-image-import">
    <div class="configurator-layer-editor-modal-image-import__body">
      <p class="configurator-layer-editor-modal-image-import__instructions">
        {{
          $t('configuratorLayerEditorModalImageImport.instructions.formats', {
            width: imageSize.width,
            height: imageSize.height,
          })
        }}
        <br />
        {{
          $t('configuratorLayerEditorModalImageImport.instructions.sizes', {
            formatList,
          })
        }}
      </p>

      <p v-if="screenPrinting">
        <span
          class="configurator-layer-editor-modal-image-import__screen-printing-warning"
          v-text="
            $t('configuratorLayerEditorModalImageImport.screenPrinting.warning')
          "
        ></span>
        <span
          v-text="
            $t('configuratorLayerEditorModalImageImport.screenPrinting.details')
          "
        ></span>
      </p>

      <p>
        <input
          :id="id"
          ref="fileInput"
          type="file"
          accept=".jpg, .jpeg, .png"
          required
          class="configurator-layer-editor-modal-image-import__file-input"
          @change="onFileChange"
        />

        <label
          :for="id"
          class="configurator-layer-editor-modal-image-import__file-label"
          v-text="$t('configuratorLayerEditorModalImageImport.fromFile.label')"
        ></label>
      </p>

      <p v-if="isDebugAiGenEnabled">
        <button
          class="configurator-layer-editor-modal-image-import__file-label"
          @click="$emit('click')"
        >
          <span>
            {{ $t('configuratorLayerEditorModalImageImport.aiGenImage.label') }}
          </span>
        </button>
      </p>

      <template v-if="fileUploadDescription">
        <p>
          <ConfiguratorBtnDropdownTrigger
            v-model="isMoreInfoVisible"
            :label="
              $t('configuratorLayerEditorModalImageImport.action.moreInfo')
            "
          />
        </p>

        <transition
          name="configurator-layer-editor-modal-image-import__more-info-"
        >
          <div
            v-if="isMoreInfoVisible"
            class="configurator-layer-editor-modal-image-import__more-info"
          >
            <!-- eslint-disable vue/no-v-html -->
            <div
              class="configurator-layer-editor-modal-image-import__more-info-container"
              v-html="fileUploadDescription"
            ></div>
            <!-- eslint-enable vue/no-v-html -->
          </div>
        </transition>
      </template>
    </div>
  </div>
</template>

<script>
import { randomStr } from '../../../../utils/String';
import { humanFileSize } from '../../../../utils/FileSize';
import {
  ImageFormatValidationError,
  ImageMimeType,
  ImageSizeValidationError,
  validateImage,
} from '../../../../utils/ImageUtils';
import ConfiguratorBtnDropdownTrigger from '../../ConfiguratorBtnDropdownTrigger';
import { mapState as mapMiscState } from '../../../../store/modules/configurator/misc';
import { mmToPx, Resolution } from '../../../../utils/MathUtils';

const UPLOAD_MAX_FILE_SIZE = 8 * 1000 * 1000; // 8Mo

export default {
  name: 'ConfiguratorLayerEditorModalImageImport',
  emits: ['click'],
  components: { ConfiguratorBtnDropdownTrigger },
  props: {
    screenPrinting: Boolean,
    zone: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      id: randomStr(),
      isMoreInfoVisible: false,
    };
  },
  computed: {
    ...mapMiscState({ fileUploadDescription: 'fileUploadDescription' }),
    allowedMimeTypes() {
      return Object.freeze({
        [ImageMimeType.JPEG]: 'JPEG',
        [ImageMimeType.PNG]: 'PNG',
      });
    },
    formatList() {
      return Object.values(this.allowedMimeTypes).reduce(
        (acc, mimeType, index, list) => {
          acc += mimeType;

          if (index < list.length - 2) {
            acc += ', ';
          } else if (index === list.length - 2) {
            acc += this.$t(
              'configuratorLayerEditorModalImageImport.instructions.glue'
            );
          }

          return acc;
        },
        ''
      );
    },
    imageSize() {
      const { width, height } = this.zone;

      return {
        width: Math.round(mmToPx(width, Resolution.PRINT)),
        height: Math.round(mmToPx(height, Resolution.PRINT)),
      };
    },
    isDebugAiGenEnabled() {
      return new URLSearchParams(globalThis.location.search).has('debugAiGen');
    },
  },
  methods: {
    async onFileChange(e) {
      const [file] = e.target.files;

      if (file && (await this.isValidImage(file))) {
        this.$emit('change', file);
      }
    },
    async isValidImage(file) {
      if (!(file instanceof File)) {
        throw new TypeError('The provided file to validate is invalid');
      }

      try {
        return await validateImage(file, {
          maxFileSize: UPLOAD_MAX_FILE_SIZE,
          mimeTypes: Object.keys(this.allowedMimeTypes),
        });
      } catch (e) {
        let errorMessage;

        if (e instanceof ImageSizeValidationError) {
          errorMessage = this.$t(
            'configuratorLayerEditorModalImageImport.file.error.invalidSize',
            {
              size: humanFileSize(UPLOAD_MAX_FILE_SIZE),
            }
          );
        } else if (e instanceof ImageFormatValidationError) {
          errorMessage = this.$t(
            'configuratorLayerEditorModalImageImport.file.error.invalidType.message',
            {
              formats: Object.values(this.allowedMimeTypes).reduce(
                (acc, v, i, a) => {
                  acc += v;

                  if (i < a.length - 2) {
                    acc += ', ';
                  } else if (i === a.length - 2) {
                    acc += this.$t(
                      'configuratorLayerEditorModalImageImport.file.error.invalidType.glue'
                    );
                  }

                  return acc;
                },
                ''
              ),
            }
          );
        } else {
          throw e;
        }

        alert(errorMessage);

        return false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.configurator-layer-editor-modal-image-import {
  $block-selector: &;
  $more-info-font-size: 0.75em;
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  background: $color-white;
  border-top: solid 1px $color-gray-alto;
  color: $color-gray-dusty;
  justify-content: center;

  &__body {
    display: flex;
    flex-direction: column;
    margin: 1em;
    text-align: center;

    > *:last-child {
      margin-bottom: 0;
    }

    &::before {
      $icon-size: 6.25em;
      @extend %icon;
      @extend %icon--graphism;
      display: block;
      font-size: $icon-size;
      margin-bottom: math.div(1em, parse-int($icon-size));
      color: $color-primary;
    }
  }

  &__screen-printing-warning {
    font-weight: bold;
    color: $color-secondary;
    display: block;
    font-style: italic;
  }

  &__file-input {
    @include sr-only();
  }

  &__file-label {
    background-color: $color-primary;
    color: $color-white;
    border-radius: 0.5em;
    padding: 1em 2em;
    text-align: center;
    display: inline-flex;
    flex-direction: column;
    align-items: center;
    font-weight: bold;
    cursor: pointer;
    border: solid 1px $color-primary;
    transition-property: color, background-color;
    transition-duration: $default-transition-duration;

    &:hover {
      color: $color-primary;
      background-color: transparent;
    }

    #{$block-selector}__file-input:active + & {
      cursor: wait; // Set wait cursor while browser is opening the file picker
    }
  }

  &__more-info {
    text-align: left;
    overflow: hidden; // Break margin fusion
    color: $color-gray-dusty;
    background-color: $color-gray-whild-sand;
    margin-bottom: 1em;
    font-size: 0.75em;

    &--enter,
    &--leave-to {
      opacity: 0;
      transform: translateY(-1em);
    }

    &--enter-active,
    &--leave-active {
      transition-property: opacity, transform;
      transition-duration: $default-transition-duration;
    }
  }

  &__more-info-container {
    margin: math.div(1em, parse-int($more-info-font-size));
  }
}
</style>
